/**
 * History Student Notes - Study Materials
 * Years 11-13 History (Senior Secondary)
 * Based on Vanuatu National Syllabus
 */

// ============================================
// YEAR 11 HISTORY STUDENT NOTES
// ============================================
export const year11HistoryStudentNotes = {
  strand1: {
    title: "Skills of History",
    overview: "Learn essential historical skills including source analysis, research methods, and historical enquiry.",
    units: [
      {
        id: "1.1",
        title: "Personalizing History",
        objectives: [
          "Understand the relationship between personal history and community history",
          "Research family and village historical origins",
          "Explain how past events shape the present"
        ],
        keyTerms: [
          { term: "Oral History", definition: "History passed down through spoken word from generation to generation" },
          { term: "Family History", definition: "The historical record of a family's origins, relationships, and events" },
          { term: "Historical Significance", definition: "The importance of an event or person in history" }
        ],
        studyNotes: `
## Personalizing History

### Why Study Personal History?
- Connects you to your roots and identity
- Helps understand community traditions
- Shows how past events affect present life

### Researching Your History
1. **Interview family elders** - Ask about origins, traditions, meanings
2. **Document oral traditions** - Record stories before they are lost
3. **Analyze findings** - Look for patterns and meanings

### Historical Meanings
- Village names often have historical origins
- Family names may indicate clan connections
- Traditional practices have historical roots

### Key Questions to Ask
- What does my village name mean?
- Where did my ancestors come from?
- What traditions have been passed down?
        `,
        examTips: [
          "Be able to explain the significance of studying personal history",
          "Know how to conduct basic historical research",
          "Understand the connection between past and present"
        ],
        practiceQuestions: [
          { question: "Why is it important to study personal and family history?", answer: "It connects us to our roots, helps understand traditions, and shows how past events shape the present" },
          { question: "What is oral history?", answer: "History passed down through spoken word from generation to generation" },
          { question: "Name two ways to research family history", answer: "Interview family elders, document oral traditions" }
        ]
      },
      {
        id: "1.2",
        title: "Historical Sources",
        objectives: [
          "Define and identify different types of historical sources",
          "Distinguish between primary and secondary sources",
          "Understand oral traditions and testimonies"
        ],
        keyTerms: [
          { term: "Primary Source", definition: "Original, first-hand account or document from the time period being studied" },
          { term: "Secondary Source", definition: "An interpretation or analysis of primary sources, created after the event" },
          { term: "Oral Tradition", definition: "Cultural knowledge passed down through generations by word of mouth" }
        ],
        studyNotes: `
## Historical Sources

### Types of Sources

#### Primary Sources (Original)
- Letters, diaries, journals
- Photographs, artifacts
- Government records
- Oral testimonies from witnesses

#### Secondary Sources (Interpreted)
- History textbooks
- Documentaries
- Scholarly articles
- Encyclopedias

### Oral Sources in Vanuatu
- **Oral Traditions:** Stories, customs, practices passed down
- **Oral Testimonies:** First-hand accounts from witnesses

### Evaluating Sources
1. Who created it? (Author/creator)
2. When was it created? (Date)
3. Why was it created? (Purpose)
4. Is it reliable? (Bias check)
        `,
        examTips: [
          "Know the difference between primary and secondary sources",
          "Be able to give examples of each type",
          "Understand how to evaluate source reliability"
        ],
        practiceQuestions: [
          { question: "What is a primary source?", answer: "An original, first-hand account or document from the time period being studied" },
          { question: "Give two examples of secondary sources", answer: "History textbooks, documentaries, encyclopedias" },
          { question: "What is the difference between oral tradition and oral testimony?", answer: "Oral tradition is knowledge passed through generations; oral testimony is a first-hand account from a witness" }
        ]
      },
      {
        id: "1.3",
        title: "Historical Enquiry",
        objectives: [
          "Understand the process of historical enquiry",
          "Formulate research questions and hypotheses",
          "Collect and analyze historical data"
        ],
        keyTerms: [
          { term: "Historical Enquiry", definition: "The process of investigating and answering questions about the past" },
          { term: "Hypothesis", definition: "A proposed explanation based on limited evidence, to be tested" },
          { term: "Evidence", definition: "Information used to support or disprove a historical claim" }
        ],
        studyNotes: `
## Historical Enquiry Process

### Steps of Historical Enquiry
1. **Ask a question** - What do you want to know?
2. **Research** - Gather information from various sources
3. **Analyze** - Examine and interpret the data
4. **Hypothesize** - Form a possible explanation
5. **Test** - Look for evidence that supports or refutes
6. **Conclude** - Draw conclusions based on evidence
7. **Report** - Share findings

### Formulating Good Questions
- Be specific and focused
- Can be answered with evidence
- Lead to deeper understanding

### Examples
- Why did Vanuatu's independence occur in 1980?
- How did Christianity change Vanuatu society?
- What caused the population to migrate?
        `,
        examTips: [
          "Know the steps of historical enquiry",
          "Be able to formulate good research questions",
          "Understand how to test a hypothesis"
        ],
        practiceQuestions: [
          { question: "What is historical enquiry?", answer: "The process of investigating and answering questions about the past" },
          { question: "List the main steps of historical enquiry", answer: "Ask a question, research, analyze, hypothesize, test, conclude, report" },
          { question: "What makes a good historical research question?", answer: "It should be specific, answerable with evidence, and lead to deeper understanding" }
        ]
      }
    ]
  },
  strand2: {
    title: "Early History of Vanuatu",
    overview: "Explore the origins of Vanuatu's people, early societies, and traditional governance systems.",
    units: [
      {
        id: "2.1",
        title: "Origins and Migration",
        objectives: [
          "Understand theories about the origins of Pacific peoples",
          "Learn about migration routes to Vanuatu",
          "Explore Lapita culture and its significance"
        ],
        keyTerms: [
          { term: "Lapita", definition: "Ancient Pacific culture known for distinctive pottery, ancestors of Melanesians" },
          { term: "Migration", definition: "The movement of people from one place to another" },
          { term: "Melanesia", definition: "Pacific region including Vanuatu, characterized by dark-skinned peoples" }
        ],
        studyNotes: `
## Origins of Vanuatu's People

### Migration Theories
1. **Out of Africa** - Human origins in Africa
2. **Asian dispersal** - Movement through Southeast Asia
3. **Pacific settlement** - Lapita culture spread through islands

### Lapita Culture (c. 1500-500 BCE)
- Distinctive decorated pottery
- Skilled navigators and sailors
- Ancestor culture of modern Melanesians
- Settled many Pacific islands

### Evidence of Early Settlement
- Archaeological sites
- Lapita pottery fragments
- Ancient tools and artifacts
- Oral traditions

### Vanuatu's Geographic Position
- Part of Melanesia
- Over 80 islands
- Diverse languages and cultures
- Strategic Pacific location
        `,
        examTips: [
          "Know the Lapita culture and its significance",
          "Understand basic migration patterns to the Pacific",
          "Be able to explain archaeological evidence"
        ],
        practiceQuestions: [
          { question: "What is Lapita culture?", answer: "Ancient Pacific culture known for distinctive pottery, ancestors of Melanesians" },
          { question: "What evidence shows early settlement in Vanuatu?", answer: "Archaeological sites, Lapita pottery fragments, ancient tools, oral traditions" },
          { question: "What region is Vanuatu part of?", answer: "Melanesia" }
        ]
      },
      {
        id: "2.2",
        title: "Traditional Society",
        objectives: [
          "Understand traditional social structures",
          "Learn about the chief system and governance",
          "Explore kastom and its role in society"
        ],
        keyTerms: [
          { term: "Kastom", definition: "Traditional customs, beliefs, and practices of ni-Vanuatu people" },
          { term: "Chief", definition: "Traditional leader responsible for community governance and disputes" },
          { term: "Nakamal", definition: "Traditional meeting house for community gatherings and ceremonies" }
        ],
        studyNotes: `
## Traditional Vanuatu Society

### Social Structure
- **Chiefs** - Community leaders
- **Elders** - Wisdom keepers
- **Families** - Basic social unit
- **Clans** - Extended family groups

### The Chief System
- Chiefs earned status through merit
- Responsible for:
  - Settling disputes
  - Leading ceremonies
  - Protecting community
  - Maintaining kastom

### Kastom (Custom)
- Traditional laws and practices
- Governs social behavior
- Includes ceremonies, taboos, traditions
- Passed down through generations

### Traditional Governance
- Decisions made by consensus
- Chiefs consulted elders
- Community assemblies at nakamal
- Oral law rather than written
        `,
        examTips: [
          "Understand the role of chiefs in traditional society",
          "Know what kastom means and its importance",
          "Be able to describe traditional social structures"
        ],
        practiceQuestions: [
          { question: "What is kastom?", answer: "Traditional customs, beliefs, and practices of ni-Vanuatu people" },
          { question: "What were the responsibilities of a traditional chief?", answer: "Settling disputes, leading ceremonies, protecting community, maintaining kastom" },
          { question: "What is a nakamal?", answer: "A traditional meeting house for community gatherings and ceremonies" }
        ]
      }
    ]
  },
  strand3: {
    title: "European Contact and Colonialism",
    overview: "Study the arrival of Europeans, the Condominium period, and its impacts on Vanuatu.",
    units: [
      {
        id: "3.1",
        title: "Early European Contact",
        objectives: [
          "Learn about early European explorers",
          "Understand the impact of missionaries",
          "Examine the effects of sandalwood and labor trade"
        ],
        keyTerms: [
          { term: "Blackbirding", definition: "The practice of recruiting Pacific Islanders to work on plantations, often through deception or force" },
          { term: "Sandalwood Trade", definition: "Trade in valuable sandalwood timber that brought early European contact" },
          { term: "Missionary", definition: "Religious workers who came to convert Pacific peoples to Christianity" }
        ],
        studyNotes: `
## European Contact Period

### Early Explorers
- **Pedro Fernandes de Queirós (1606)** - First European to reach Vanuatu
- Named the island "Espíritu Santo"
- Later visits by British and French explorers

### Missionaries
- Arrived in the 1800s
- Brought Christianity
- Established schools and churches
- Changed traditional practices

### Sandalwood Trade (1840s-1860s)
- Europeans sought valuable sandalwood
- Introduced trade goods
- Caused conflicts with local people

### Blackbirding (1860s-1900s)
- Ni-Vanuatu recruited for plantations
- Often through deception or force
- Worked in Queensland, Fiji, Samoa
- Many never returned home
        `,
        examTips: [
          "Know key dates and figures in European contact",
          "Understand the impact of blackbirding",
          "Be able to explain how missionaries changed society"
        ],
        practiceQuestions: [
          { question: "Who was the first European to reach Vanuatu?", answer: "Pedro Fernandes de Queirós in 1606" },
          { question: "What was blackbirding?", answer: "The practice of recruiting Pacific Islanders to work on plantations, often through deception or force" },
          { question: "What trade brought early European contact?", answer: "The sandalwood trade" }
        ]
      },
      {
        id: "3.2",
        title: "The Condominium Period",
        objectives: [
          "Understand the Anglo-French Condominium",
          "Examine dual colonial administration",
          "Analyze impacts on ni-Vanuatu people"
        ],
        keyTerms: [
          { term: "Condominium", definition: "Joint colonial rule of Vanuatu by Britain and France (1906-1980)" },
          { term: "New Hebrides", definition: "Colonial name for Vanuatu during the Condominium period" },
          { term: "Dual Administration", definition: "The system of having both British and French laws and institutions" }
        ],
        studyNotes: `
## The Condominium (1906-1980)

### What Was the Condominium?
- Joint colonial rule by Britain and France
- Also called "Pandemonium" due to confusion
- Vanuatu called "New Hebrides"

### Dual Administration
- Two sets of laws
- Two police forces
- Two school systems (English and French)
- Two court systems
- Ni-Vanuatu had no political rights

### Impacts on Ni-Vanuatu
- Loss of land to settlers
- Forced to work on plantations
- Traditional practices suppressed
- Divided education system
- No representation in government

### The Joint Court
- Mixed tribunal for disputes
- Used Spanish judge as neutral party
- Often confusing and slow
        `,
        examTips: [
          "Know what the Condominium was and who was involved",
          "Understand the challenges of dual administration",
          "Be able to explain impacts on ni-Vanuatu people"
        ],
        practiceQuestions: [
          { question: "What was the Condominium?", answer: "Joint colonial rule of Vanuatu by Britain and France from 1906-1980" },
          { question: "Why was it called the 'Pandemonium'?", answer: "Because of the confusion caused by having two separate colonial administrations" },
          { question: "What were some impacts on ni-Vanuatu people?", answer: "Loss of land, forced labor, suppressed traditions, divided education, no political rights" }
        ]
      }
    ]
  }
};

// ============================================
// YEAR 12 HISTORY STUDENT NOTES
// ============================================
export const year12HistoryStudentNotes = {
  strand1: {
    title: "World War II in the Pacific",
    overview: "Study the impact of World War II on Vanuatu and the Pacific region.",
    units: [
      {
        id: "1.1",
        title: "WWII Arrives in the Pacific",
        objectives: [
          "Understand the causes of WWII in the Pacific",
          "Learn about major Pacific battles",
          "Examine the strategic importance of Vanuatu"
        ],
        keyTerms: [
          { term: "Pacific War", definition: "The theater of World War II fought in the Pacific Ocean and islands" },
          { term: "Allied Forces", definition: "Countries fighting against Japan including USA, Australia, Britain" },
          { term: "Military Base", definition: "A facility for military operations and personnel" }
        ],
        studyNotes: `
## World War II in the Pacific

### The Pacific War (1941-1945)
- Japan attacked Pearl Harbor (December 1941)
- War spread across the Pacific
- Allied forces fought to stop Japanese expansion

### Vanuatu's Strategic Importance
- Located on supply routes to Australia
- US established bases on Efate and Espíritu Santo
- Over 100,000 American troops stationed
- Million Dollar Point - Santo

### Major Bases in Vanuatu
- **Espíritu Santo:** Largest US base, hospital, airfields
- **Efate:** Naval base, airfields
- Used for operations against Japanese

### Impact on Local Population
- Exposure to American culture
- Economic changes (paid work)
- Introduction of modern goods
- Changed perceptions of white people
        `,
        examTips: [
          "Know why Vanuatu was strategically important",
          "Understand the scale of US presence",
          "Be able to describe impacts on local people"
        ],
        practiceQuestions: [
          { question: "Why was Vanuatu strategically important in WWII?", answer: "It was on supply routes to Australia and was used to stop Japanese expansion" },
          { question: "Where were the main US bases in Vanuatu?", answer: "Espíritu Santo and Efate" },
          { question: "Approximately how many US troops were stationed in Vanuatu?", answer: "Over 100,000" }
        ]
      },
      {
        id: "1.2",
        title: "Social and Political Awakening",
        objectives: [
          "Understand how WWII changed ni-Vanuatu perspectives",
          "Learn about the John Frum movement",
          "Examine the rise of political consciousness"
        ],
        keyTerms: [
          { term: "Cargo Cult", definition: "Movements believing that rituals would bring material wealth like that of foreigners" },
          { term: "John Frum", definition: "A movement on Tanna that emerged during/after WWII" },
          { term: "Political Consciousness", definition: "Awareness of political issues and rights" }
        ],
        studyNotes: `
## Social and Political Awakening

### Changed Perspectives
- Ni-Vanuatu saw white people as ordinary humans
- Americans treated ni-Vanuatu more equally
- Exposure to democracy and equality ideas
- Questioned colonial rule

### The John Frum Movement
- Emerged on Tanna island
- Blend of traditional beliefs and new ideas
- Rejection of colonial/missionary control
- Belief in a coming messiah figure
- Still exists today

### Seeds of Independence
- WWII planted ideas of freedom
- Ni-Vanuatu began questioning colonialism
- Traditional leaders gained confidence
- Formation of early political awareness
        `,
        examTips: [
          "Understand how WWII changed attitudes",
          "Know about the John Frum movement",
          "Be able to explain the connection to later independence"
        ],
        practiceQuestions: [
          { question: "How did WWII change ni-Vanuatu perspectives?", answer: "They saw white people as ordinary, were treated more equally, and were exposed to ideas of democracy" },
          { question: "What is the John Frum movement?", answer: "A movement on Tanna that emerged during/after WWII, blending traditional beliefs with new ideas and rejecting colonial control" },
          { question: "How did WWII contribute to independence movements?", answer: "It planted ideas of freedom, made people question colonialism, and gave traditional leaders confidence" }
        ]
      }
    ]
  },
  strand2: {
    title: "Independence Movement",
    overview: "Study the path to Vanuatu's independence and the key figures involved.",
    units: [
      {
        id: "2.1",
        title: "Rise of Political Parties",
        objectives: [
          "Understand the formation of political parties",
          "Learn about key independence leaders",
          "Examine the role of the Vanua'aku Pati"
        ],
        keyTerms: [
          { term: "Vanua'aku Pati", definition: "The main independence party, led by Father Walter Lini" },
          { term: "NHNP", definition: "New Hebrides National Party, later became Vanua'aku Pati" },
          { term: "Self-determination", definition: "The right of a people to determine their own government" }
        ],
        studyNotes: `
## Rise of Political Parties

### Early Political Organizations
- 1970s: Political awareness grew
- Churches played important role
- Chiefs began organizing

### Vanua'aku Pati (Our Land Party)
- Originally New Hebrides National Party
- Led by **Father Walter Lini**
- Advocated for independence
- Wanted return of land to ni-Vanuatu

### Key Independence Leaders
- **Walter Lini** - First Prime Minister
- **Barak Sopé** - Political activist
- **Donald Kalpokas** - Independence fighter

### Goals of the Movement
1. End colonial rule
2. Return land to ni-Vanuatu
3. Preserve kastom
4. Create united nation
        `,
        examTips: [
          "Know the key political parties and leaders",
          "Understand the goals of the independence movement",
          "Be able to explain the role of churches"
        ],
        practiceQuestions: [
          { question: "What was the Vanua'aku Pati?", answer: "The main independence party, led by Father Walter Lini" },
          { question: "Who became Vanuatu's first Prime Minister?", answer: "Father Walter Lini" },
          { question: "What were the goals of the independence movement?", answer: "End colonial rule, return land to ni-Vanuatu, preserve kastom, create a united nation" }
        ]
      },
      {
        id: "2.2",
        title: "Independence Day - July 30, 1980",
        objectives: [
          "Understand the events leading to independence",
          "Learn about the Santo Rebellion",
          "Examine the significance of independence"
        ],
        keyTerms: [
          { term: "Independence", definition: "Freedom from colonial rule, achieved July 30, 1980" },
          { term: "Santo Rebellion", definition: "Attempt by Jimmy Stevens to prevent independence on Santo" },
          { term: "Constitution", definition: "The fundamental law establishing Vanuatu's government" }
        ],
        studyNotes: `
## Independence Day

### Road to Independence
- 1979: Elections held
- Vanua'aku Pati won majority
- Independence date set: July 30, 1980

### The Santo Rebellion
- **Jimmy Stevens** led rebellion
- Attempted secession of Santo
- Supported by some French settlers
- Put down by Papua New Guinea troops
- Stevens imprisoned

### Independence Day - July 30, 1980
- Flag raised at midnight
- Walter Lini became Prime Minister
- "New Hebrides" became "Vanuatu"
- ("Land Eternal" in local languages)

### Significance
- End of 74 years of Condominium
- First ni-Vanuatu government
- Land returned to traditional owners
- Kastom officially recognized
        `,
        examTips: [
          "Know the date of independence",
          "Understand the Santo Rebellion",
          "Be able to explain the significance of independence"
        ],
        practiceQuestions: [
          { question: "When did Vanuatu gain independence?", answer: "July 30, 1980" },
          { question: "What was the Santo Rebellion?", answer: "An attempt by Jimmy Stevens to prevent independence on Santo, supported by some French settlers" },
          { question: "What does 'Vanuatu' mean?", answer: "Land Eternal" }
        ]
      }
    ]
  }
};

// ============================================
// YEAR 13 HISTORY STUDENT NOTES
// ============================================
export const year13HistoryStudentNotes = {
  strand1: {
    title: "Post-Independence Vanuatu",
    overview: "Examine Vanuatu's development as an independent nation and its challenges.",
    units: [
      {
        id: "1.1",
        title: "Building a New Nation",
        objectives: [
          "Understand challenges of nation-building",
          "Learn about government structure",
          "Examine land reform policies"
        ],
        keyTerms: [
          { term: "Parliament", definition: "Vanuatu's legislative body with elected representatives" },
          { term: "Land Reform", definition: "Policies to return land ownership to ni-Vanuatu people" },
          { term: "National Unity", definition: "Bringing together diverse islands and languages as one nation" }
        ],
        studyNotes: `
## Building a New Nation

### Challenges After Independence
- Uniting 80+ islands
- Over 100 languages
- Divided education legacy (English/French)
- Need to develop economy
- Creating national identity

### Government Structure
- **Parliament:** Elected representatives
- **President:** Head of state (ceremonial)
- **Prime Minister:** Head of government
- **Council of Chiefs:** Advisory on kastom

### Land Reform
- Constitution: "Land belongs to custom owners"
- Land returned to traditional owners
- Leasehold system for development
- Ongoing issues with land disputes

### Achievements
- Stable democracy
- Maintained peace
- Preserved kastom
- Developed tourism
        `,
        examTips: [
          "Know the structure of Vanuatu's government",
          "Understand land reform policies",
          "Be able to discuss challenges and achievements"
        ],
        practiceQuestions: [
          { question: "What are the main challenges Vanuatu faced after independence?", answer: "Uniting diverse islands, multiple languages, divided education system, economic development, creating national identity" },
          { question: "Who is the head of government in Vanuatu?", answer: "The Prime Minister" },
          { question: "What does the Constitution say about land?", answer: "Land belongs to custom owners" }
        ]
      },
      {
        id: "1.2",
        title: "Vanuatu in the Pacific Community",
        objectives: [
          "Understand Vanuatu's regional relationships",
          "Learn about Pacific organizations",
          "Examine Vanuatu's international role"
        ],
        keyTerms: [
          { term: "Pacific Islands Forum", definition: "Regional organization of Pacific island nations" },
          { term: "MSG", definition: "Melanesian Spearhead Group - alliance of Melanesian nations" },
          { term: "Climate Change", definition: "Global environmental issue particularly affecting Pacific islands" }
        ],
        studyNotes: `
## Vanuatu in the Pacific Community

### Regional Organizations
- **Pacific Islands Forum:** Regional cooperation
- **Melanesian Spearhead Group (MSG):** 
  - Vanuatu, PNG, Solomon Islands, Fiji, FLNKS
  - Promotes Melanesian solidarity

### International Relations
- Member of United Nations (1981)
- Member of the Commonwealth
- Member of La Francophonie
- Relations with Australia, New Zealand, France

### Key Issues
1. **Climate Change**
   - Rising sea levels
   - Extreme weather events
   - Vanuatu is a leading voice

2. **West Papua**
   - Vanuatu supports self-determination
   - MSG involvement

3. **Economic Development**
   - Aid from donors
   - Tourism development
   - Sustainable growth
        `,
        examTips: [
          "Know the regional organizations Vanuatu belongs to",
          "Understand Vanuatu's stance on climate change",
          "Be able to discuss international relationships"
        ],
        practiceQuestions: [
          { question: "What is the Melanesian Spearhead Group?", answer: "An alliance of Melanesian nations including Vanuatu, PNG, Solomon Islands, Fiji, and FLNKS" },
          { question: "Why is climate change important to Vanuatu?", answer: "Vanuatu is directly affected by rising sea levels and extreme weather, and is a leading voice on the issue" },
          { question: "When did Vanuatu join the United Nations?", answer: "1981" }
        ]
      }
    ]
  },
  strand2: {
    title: "Contemporary Issues",
    overview: "Examine current challenges and opportunities facing Vanuatu.",
    units: [
      {
        id: "2.1",
        title: "Preserving Kastom in Modern Times",
        objectives: [
          "Understand the role of kastom today",
          "Examine tensions between tradition and modernity",
          "Learn about cultural preservation efforts"
        ],
        keyTerms: [
          { term: "Kastom", definition: "Traditional customs and practices that continue to guide ni-Vanuatu life" },
          { term: "Vanuatu Cultural Centre", definition: "Institution dedicated to preserving Vanuatu's cultural heritage" },
          { term: "Intangible Heritage", definition: "Cultural practices, knowledge, and traditions" }
        ],
        studyNotes: `
## Preserving Kastom in Modern Times

### Kastom Today
- Still central to ni-Vanuatu identity
- Recognized in the Constitution
- Council of Chiefs advises government
- Kastom ceremonies continue

### Tensions and Challenges
- Globalization and outside influences
- Youth attracted to modern culture
- Some traditions declining
- Language loss concerns

### Preservation Efforts
- **Vanuatu Cultural Centre**
  - Documents traditions
  - Supports field workers
  - Preserves languages

- **Education**
  - Vernacular languages in schools
  - Kastom in curriculum

### Finding Balance
- Adapting kastom to modern needs
- Keeping valuable traditions alive
- Allowing positive change
        `,
        examTips: [
          "Understand the role of kastom in modern Vanuatu",
          "Know about preservation efforts",
          "Be able to discuss challenges and solutions"
        ],
        practiceQuestions: [
          { question: "How is kastom recognized in Vanuatu's government?", answer: "It's recognized in the Constitution and the Council of Chiefs advises the government" },
          { question: "What is the Vanuatu Cultural Centre?", answer: "An institution dedicated to preserving Vanuatu's cultural heritage" },
          { question: "What are some challenges to preserving kastom?", answer: "Globalization, youth attracted to modern culture, declining traditions, language loss" }
        ]
      },
      {
        id: "2.2",
        title: "Future Challenges and Opportunities",
        objectives: [
          "Analyze current development challenges",
          "Examine economic opportunities",
          "Discuss Vanuatu's future prospects"
        ],
        keyTerms: [
          { term: "Sustainable Development", definition: "Development that meets present needs without compromising future generations" },
          { term: "LDC Graduation", definition: "Moving from Least Developed Country status" },
          { term: "Diversification", definition: "Developing multiple economic sectors" }
        ],
        studyNotes: `
## Future Challenges and Opportunities

### Key Challenges
1. **Climate Change**
   - Rising seas, cyclones
   - Need for adaptation
   - International advocacy

2. **Economic Development**
   - Small market size
   - Geographic isolation
   - Infrastructure needs

3. **Social Issues**
   - Education quality
   - Health services
   - Youth employment

### Opportunities
1. **Tourism**
   - Natural beauty
   - Cultural attractions
   - Sustainable tourism

2. **Agriculture**
   - Organic products
   - Kava exports
   - Food security

3. **Blue Economy**
   - Fisheries management
   - Marine resources
   - Conservation

### Vision for the Future
- Sustainable development
- Preserve kastom and environment
- Improve quality of life
- Play active regional role
        `,
        examTips: [
          "Know the main challenges facing Vanuatu",
          "Understand economic opportunities",
          "Be able to discuss sustainable development"
        ],
        practiceQuestions: [
          { question: "What are three key challenges facing Vanuatu?", answer: "Climate change, economic development, and social issues" },
          { question: "What economic opportunities does Vanuatu have?", answer: "Tourism, agriculture (organic products, kava), and the blue economy (fisheries, marine resources)" },
          { question: "What is sustainable development?", answer: "Development that meets present needs without compromising future generations" }
        ]
      }
    ]
  }
};

export default {
  year11HistoryStudentNotes,
  year12HistoryStudentNotes,
  year13HistoryStudentNotes
};
